/***** frontend/src/pages/EditDevice.js *****/
import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import validator from 'validator';
import DOMPurify from 'dompurify';
import api from '../api'; // 你封装的 axios 实例

const EditDevice = () => {
  const { id } = useParams(); // 当前设备 _id
  const navigate = useNavigate();

  // 设备表单数据
  const [formData, setFormData] = useState({
    deviceId: '',
    name: '',
    location: '',
    description: '',
    tcpAddress: '',
    videoStreamAddress: '',
    status: '可用',
    isIdle: true,
    ratio: '1',         // 上/下分比
    coverImageUrl: '',  // 后端返回的封面地址，如 "/uploads/xxx"
    // ===== 已有: 每转分数 & 下分比例 =====
    turnCost: '2.8',
    fsFactor: '0.145',    // ===== [新增] 损耗百分比 =====
    lossPercentage: '0',

    // ===== [新增] 抽水功能配置 =====
    drawWaterEnabled: false,
    drawWaterBaseTurns: '10',
    drawWaterTurns: '1'
  });

  // 本地待上传的图片文件
  const [coverFile, setCoverFile] = useState(null);

  // 其他状态
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);  // 表单提交中
  const [fetching, setFetching] = useState(true); // 拉取设备信息中
  const [partitions, setPartitions] = useState([]);
  const [fetchingPartitions, setFetchingPartitions] = useState(true);

  // ============ 1) componentDidMount-like ============
  useEffect(() => {
    fetchDevice();
    fetchPartitions();
    // eslint-disable-next-line
  }, [id]);

  // ============ 2) 拉取设备信息 ============
  const fetchDevice = async () => {
    setFetching(true);
    try {
      const res = await api.get(`/api/devices/${id}`);
      const data = res.data;
      // 当后端没有 lossPercentage 时，这里可默认'0'
      setFormData((prev) => ({
        ...prev,
        deviceId: data.deviceId,
        name: data.name,
        location: data.location ? data.location._id : '',
        description: data.description || '',
        tcpAddress: data.tcpAddress,
        videoStreamAddress: data.videoStreamAddress,
        status: data.status,
        isIdle: data.isIdle,
        ratio: String(data.ratio ?? '1'),
        coverImageUrl: data.coverImageUrl || '',
        turnCost: data.turnCost !== undefined ? String(data.turnCost) : '2.8',
        fsFactor: data.fsFactor !== undefined ? String(data.fsFactor) : '0.145',        lossPercentage: data.lossPercentage !== undefined ? String(data.lossPercentage) : '0',
        // 修复: 拉取 type 字段
        type: data.type || 'pinball',
        // [修复] 抽水配置字段 - 正确处理布尔值
        drawWaterEnabled: data.drawWaterEnabled === true,
        drawWaterBaseTurns: data.drawWaterBaseTurns !== undefined ? String(data.drawWaterBaseTurns) : '10',
        drawWaterTurns: data.drawWaterTurns !== undefined ? String(data.drawWaterTurns) : '1',
      }));
    } catch (error) {
      console.error('获取设备信息失败:', error);
      toast.error(error.response?.data?.message || '获取设备信息失败');
      navigate('/admin/devices'); // 返回设备列表
    } finally {
      setFetching(false);
    }
  };

  // ============ 3) 拉取分区列表 ============
  const fetchPartitions = async () => {
    setFetchingPartitions(true);
    try {
      const res = await api.get('/api/partitions');
      setPartitions(res.data);
    } catch (error) {
      console.error('获取分区列表失败:', error);
      toast.error(error.response?.data?.message || '获取分区列表失败');
    } finally {
      setFetchingPartitions(false);
    }
  };

  // ============ 4) 本地校验 ============
  const ipPortRegex = /^(25[0-5]|2[0-4]\d|[01]?\d?\d)(\.(25[0-5]|2[0-4]\d|[01]?\d?\d)){3}:(6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]?\d{1,4})$/;

  const validate = () => {
    const newErrors = {};

    // 设备ID
    if (!formData.deviceId.trim()) {
      newErrors.deviceId = '设备ID是必填项';
    }
    // 设备名称
    if (!formData.name.trim()) {
      newErrors.name = '设备名称是必填项';
    }
    // 分区
    if (!formData.location) {
      newErrors.location = '请选择分区';
    }
    // TCP地址
    if (!formData.tcpAddress.trim()) {
      newErrors.tcpAddress = 'TCP地址是必填项';
    } else if (!ipPortRegex.test(formData.tcpAddress)) {
      newErrors.tcpAddress = 'TCP地址格式不正确 (如 192.168.1.10:8080)';
    }
    // 视频流
    if (!formData.videoStreamAddress.trim()) {
      newErrors.videoStreamAddress = '视频流地址必填';
    } else if (!validator.isURL(formData.videoStreamAddress, { require_protocol: true, protocols: ['http','https','webrtc'] })) {
      newErrors.videoStreamAddress = '视频流地址URL格式不正确（仅支持 http、https、webrtc）';
    }
    // ratio
    const ratioNum = Number(formData.ratio);
    if (![1, 2, 3, 4, 5].includes(ratioNum)) {
      newErrors.ratio = '上/下分比例只能为1~5';
    }
    // 封面文件类型
    if (coverFile) {
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(coverFile.type)) {
        newErrors.coverFile = '仅支持 JPG / PNG 图片文件';
      }
    }

    // turnCost / fsFactor
    const costNum = parseFloat(formData.turnCost);
    const fsNum   = parseFloat(formData.fsFactor);
    if (Number.isNaN(costNum) || costNum < 0) {
      newErrors.turnCost = '每转分数必须是一个非负数字';
    }
    if (Number.isNaN(fsNum) || fsNum < 0) {
      newErrors.fsFactor = '下分比例必须是一个非负数字';
    }    // =========== [新增] 对 lossPercentage 做范围判断 0~100 ===========
    const lossP = parseFloat(formData.lossPercentage);
    if (Number.isNaN(lossP) || lossP < 0 || lossP > 100) {
      newErrors.lossPercentage = '损耗百分比必须是0~100之间的数字';
    }

    // =========== [新增] 抽水参数校验 ===========
    if (formData.drawWaterEnabled) {
      const baseTurns = parseInt(formData.drawWaterBaseTurns);
      const drawTurns = parseInt(formData.drawWaterTurns);
      
      if (Number.isNaN(baseTurns) || baseTurns < 1) {
        newErrors.drawWaterBaseTurns = '基础转数必须是大于0的整数';
      }
      if (Number.isNaN(drawTurns) || drawTurns < 1) {
        newErrors.drawWaterTurns = '抽水转数必须是大于0的整数';
      }
      if (!Number.isNaN(baseTurns) && !Number.isNaN(drawTurns) && drawTurns >= baseTurns) {
        newErrors.drawWaterTurns = '抽水转数必须小于基础转数';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 表单字段变更
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // 选择封面图
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCoverFile(file);
    if (errors.coverFile) {
      setErrors((prev) => ({ ...prev, coverFile: undefined }));
    }
  };

  // ============ 5) 提交 ============
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('请修正表单中的错误');
      return;
    }

    setLoading(true);
    try {
      // 1) 更新文字字段
      const sanitizedData = {
        ...formData,
        description: DOMPurify.sanitize(formData.description),
        ratio: Number(formData.ratio),
        turnCost: parseFloat(formData.turnCost),
        fsFactor: parseFloat(formData.fsFactor),        lossPercentage: parseFloat(formData.lossPercentage),
        // 修复: 提交 type 字段
        type: formData.type || 'pinball',
        // [新增] 抽水配置字段
        drawWaterEnabled: formData.drawWaterEnabled,
        drawWaterBaseTurns: formData.drawWaterEnabled ? parseInt(formData.drawWaterBaseTurns) : 10,
        drawWaterTurns: formData.drawWaterEnabled ? parseInt(formData.drawWaterTurns) : 1,
      };
      await api.put(`/api/devices/${id}`, sanitizedData);

      // 2) 如果选择了封面 => 上传
      if (coverFile) {
        const fd = new FormData();
        fd.append('cover', coverFile); // 后端路由中间件对应字段 'cover'

        const res = await api.post(`/api/devices/${id}/cover`, fd);
        toast.info('封面上传成功');

        if (res.data.coverImageUrl) {
          setFormData((prev) => ({
            ...prev,
            coverImageUrl: res.data.coverImageUrl
          }));
        }
      }

      toast.success('设备更新成功');
      navigate('/admin/devices');
    } catch (err) {
      console.error('更新设备失败:', err);
      // 如果后端返回 400 验证错误，则将字段错误显示到表单
      if (err.response?.status === 400 && err.response.data?.errors) {
        setErrors(err.response.data.errors);
        toast.error(err.response.data.message || '表单验证失败');
      } else {
        toast.error(err.response?.data?.message || '更新设备失败');
      }
      setLoading(false);
    }
  };

  // ============ 6) UI渲染 ============
  if (fetching || fetchingPartitions) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">加载中...</span>
        </Spinner>
      </Container>
    );
  }

  // 后端只存 "/uploads/xxx"，前端需带域名
  const fullCoverUrl = formData.coverImageUrl
    ? `${process.env.REACT_APP_API_BASE_URL}${formData.coverImageUrl}`
    : '';

  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md={8}>
          <h2 className="mt-4">编辑设备</h2>
          <Form onSubmit={handleSubmit}>
            
            {/* 设备ID */}
            <Form.Group controlId="formDeviceId" className="mt-3">
              <Form.Label>设备ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="请输入设备ID"
                name="deviceId"
                value={formData.deviceId}
                onChange={handleChange}
                isInvalid={!!errors.deviceId}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.deviceId}
              </Form.Control.Feedback>
            </Form.Group>

            {/* 设备名称 */}
            <Form.Group controlId="formName" className="mt-3">
              <Form.Label>设备名称</Form.Label>
              <Form.Control
                type="text"
                placeholder="请输入设备名称"
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* 分区 */}
            <Form.Group controlId="formLocation" className="mt-3">
              <Form.Label>分区</Form.Label>
              <Form.Select
                name="location"
                value={formData.location}
                onChange={handleChange}
                isInvalid={!!errors.location}
                required
              >
                <option value="">请选择分区</option>
                {partitions.map((part) => (
                  <option key={part._id} value={part._id}>
                    {part.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.location}
              </Form.Control.Feedback>
            </Form.Group>

            {/* 简介 */}
            <Form.Group controlId="formDescription" className="mt-3">
              <Form.Label>设备简介</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            {/* TCP地址 */}
            <Form.Group controlId="formTcpAddress" className="mt-3">
              <Form.Label>设备TCP地址</Form.Label>
              <Form.Control
                type="text"
                placeholder="例如: 192.168.1.10:8080"
                name="tcpAddress"
                value={formData.tcpAddress}
                onChange={handleChange}
                isInvalid={!!errors.tcpAddress}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.tcpAddress}
              </Form.Control.Feedback>
            </Form.Group>

            {/* 视频流地址 */}
            <Form.Group controlId="formVideoStreamAddress" className="mt-3">
              <Form.Label>视频流地址</Form.Label>
              <Form.Control
                type="url"
                placeholder="请输入设备视频流地址"
                name="videoStreamAddress"
                value={formData.videoStreamAddress}
                onChange={handleChange}
                isInvalid={!!errors.videoStreamAddress}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.videoStreamAddress}
              </Form.Control.Feedback>
            </Form.Group>

            {/* 状态 */}
            <Form.Group controlId="formStatus" className="mt-3">
              <Form.Label>设备状态</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="可用">可用</option>
                <option value="维修">维修</option>
                <option value="idle">idle</option>
                <option value="occupied">occupied</option>
              </Form.Select>
            </Form.Group>

            {/* 是否空闲 */}
            <Form.Group controlId="formIsIdle" className="mt-3">
              <Form.Check
                type="checkbox"
                label="设备空闲状态"
                name="isIdle"
                checked={formData.isIdle}
                onChange={handleChange}
              />
            </Form.Group>

            {/* ratio */}
            <Form.Group controlId="formRatio" className="mt-3">
              <Form.Label>上/下分比例</Form.Label>
              <Form.Select
                name="ratio"
                value={formData.ratio}
                onChange={handleChange}
                isInvalid={!!errors.ratio}
              >
                <option value="1">1 - 1</option>
                <option value="2">2 - 1</option>
                <option value="3">3 - 1</option>
                <option value="4">4 - 1</option>
                <option value="5">5 - 1</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.ratio}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                例如 “2 - 1” 表示上100扣200，下100加200
              </Form.Text>
            </Form.Group>

            {/* ===== 新增: 每转分数 & 下分比例 ===== */}
            <Form.Group controlId="formTurnCost" className="mt-3">
              <Form.Label>每转分数</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                name="turnCost"
                value={formData.turnCost}
                onChange={handleChange}
                isInvalid={!!errors.turnCost}
              />
              <Form.Control.Feedback type="invalid">
                {errors.turnCost}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                例如 2.8 表示每走转一次扣 2.8 分
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="formFsFactor" className="mt-3">
              <Form.Label>下分倍数</Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                name="fsFactor"
                value={formData.fsFactor}
                onChange={handleChange}
                isInvalid={!!errors.fsFactor}
              />
              <Form.Control.Feedback type="invalid">
                {errors.fsFactor}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                例如 0.145 表示 rawScore*(1-loss%) / 0.145
              </Form.Text>
            </Form.Group>
            {/* =================================== */}

            {/* [新增] 损耗百分比 */}
            <Form.Group controlId="formLossPercentage" className="mt-3">
              <Form.Label>损耗百分比 (0~100)</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                name="lossPercentage"
                value={formData.lossPercentage}
                onChange={handleChange}
                isInvalid={!!errors.lossPercentage}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lossPercentage}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                例如 13 表示损耗13%，下分时 rawScore × (1 - 0.13)
              </Form.Text>
            </Form.Group>

            {/* 封面图上传 */}
            <Form.Group controlId="formCoverImage" className="mt-3">
              <Form.Label>封面图片(JPG/PNG)</Form.Label>
              <Form.Control
                type="file"
                accept="image/jpeg, image/png"
                onChange={handleFileChange}
                isInvalid={!!errors.coverFile}
              />
              <Form.Control.Feedback type="invalid">
                {errors.coverFile}
              </Form.Control.Feedback>

              {/* 已存在封面预览 */}
              {formData.coverImageUrl && (
                <div style={{ marginTop: 10 }}>
                  <p>当前封面预览：</p>
                  <img
                    src={fullCoverUrl}
                    alt="封面"
                    style={{ width: '100%', maxHeight: 200, objectFit: 'contain' }}
                  />
                </div>
              )}
            </Form.Group>            {/* 设备模型 */}
            <Form.Group controlId="formDeviceType" className="mt-3">
              <Form.Label>设备模型</Form.Label>
              <Form.Select
                name="type"
                value={formData.type || 'pinball'}
                onChange={handleChange}
              >
                <option value="pinball">钢珠机</option>
                <option value="sanqi">三七机</option>
              </Form.Select>
            </Form.Group>

            {/* ===== [新增] 抽水功能配置 ===== */}
            <hr className="mt-4 mb-3" />
            <h5>抽水功能配置</h5>
            
            {/* 是否启用抽水 */}
            <Form.Group controlId="formDrawWaterEnabled" className="mt-3">
              <Form.Check
                type="checkbox"
                label="启用抽水功能"
                name="drawWaterEnabled"
                checked={formData.drawWaterEnabled}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                启用后，每走一定转数会随机抽取额外费用
              </Form.Text>
            </Form.Group>

            {/* 基础转数 */}
            {formData.drawWaterEnabled && (
              <>
                <Form.Group controlId="formDrawWaterBaseTurns" className="mt-3">
                  <Form.Label>基础转数</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    name="drawWaterBaseTurns"
                    value={formData.drawWaterBaseTurns}
                    onChange={handleChange}
                    isInvalid={!!errors.drawWaterBaseTurns}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.drawWaterBaseTurns}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    例如设置为10，表示每走10转触发一次抽水检查
                  </Form.Text>
                </Form.Group>

                {/* 抽水转数 */}
                <Form.Group controlId="formDrawWaterTurns" className="mt-3">
                  <Form.Label>抽水转数</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    name="drawWaterTurns"
                    value={formData.drawWaterTurns}
                    onChange={handleChange}
                    isInvalid={!!errors.drawWaterTurns}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.drawWaterTurns}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    例如设置为1，表示每次抽水扣除1转的费用（必须小于基础转数）
                  </Form.Text>
                </Form.Group>

                {/* 抽水说明 */}
                <div className="alert alert-info mt-3">
                  <small>
                    <strong>抽水规则说明：</strong><br/>
                    当前配置为"每{formData.drawWaterBaseTurns}转抽{formData.drawWaterTurns}转"，
                    即玩家每走{formData.drawWaterBaseTurns}转时，系统会额外扣除{formData.drawWaterTurns}转的费用作为抽水。<br/>
                    例如：每转费用2.8分，抽水{formData.drawWaterTurns}转 = {formData.drawWaterTurns} × 2.8 = {(parseFloat(formData.drawWaterTurns || 1) * parseFloat(formData.turnCost || 2.8)).toFixed(1)}分
                  </small>
                </div>
              </>
            )}
            {/* ================================= */}

            {/* 提交按钮 */}
            <Button variant="primary" type="submit" className="mt-4" disabled={loading}>
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  {' '}更新中...
                </>
              ) : (
                '更新设备'
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default EditDevice;
